/**
 * FormField — SSOT for label + control + help/error stacks inside forms.
 *
 * Every modal/form in the app composes this instead of hand-rolling the
 * label + input grouping. Keeps spacing, typography, and error affordance
 * uniform across ProfileView, RoleChangeDrawer, AccessGrants, the
 * messaging modals, etc.
 *
 * Usage:
 *   <FormField label="Reason" htmlFor="rd-reason" required>
 *       <textarea id="rd-reason" ... />
 *   </FormField>
 *
 *   <FormField label="Mentor id" htmlFor="rd-mentor" help="Leave blank to clear">
 *       <input id="rd-mentor" type="number" ... />
 *   </FormField>
 */

import type { ReactNode } from 'react';
import './FormField.css';

export type FormFieldProps = {
    label: ReactNode;
    /** The control's id — wired to the label via `htmlFor`. Required so
     *  screen readers + click-to-focus work. */
    htmlFor?: string;
    children: ReactNode;
    /** Short helper line below the control. Mutually exclusive with
     *  `error`; error wins if both provided. */
    help?: ReactNode;
    /** Inline validation/error message. Sets aria-invalid on the
     *  control wrapper so assistive tech announces the failure. */
    error?: ReactNode;
    /** Renders a muted `*` next to the label — purely visual, doesn't
     *  add HTML `required`. */
    required?: boolean;
    /** Visually hide the label (still accessible to screen readers).
     *  Use for toolbar filters where the placeholder + context make the
     *  label redundant visually but assistive tech still needs it. */
    hideLabel?: boolean;
    className?: string;
};

export default function FormField({
    label,
    htmlFor,
    children,
    help,
    error,
    required,
    hideLabel,
    className = '',
}: FormFieldProps) {
    const hasError = Boolean(error);
    return (
        <div
            className={`ui-form-field ${hasError ? 'ui-form-field--invalid' : ''} ${className}`.trim()}
        >
            <label
                className={`ui-form-field__label ${hideLabel ? 'ui-form-field__label--sr-only' : ''}`.trim()}
                htmlFor={htmlFor}
            >
                {label}
                {required && (
                    <span className="ui-form-field__required" aria-hidden="true">*</span>
                )}
            </label>
            <div className="ui-form-field__control">{children}</div>
            {hasError ? (
                <p className="ui-form-field__error" role="alert">{error}</p>
            ) : help ? (
                <p className="ui-form-field__help">{help}</p>
            ) : null}
        </div>
    );
}
